import { Injectable, Scopes, ControllerProtocol } from '@visiba/instantiation';
import { CellulaFormSubmitData } from '@cellula/react';
import { BasketApiService } from '@api_patient/basket_api.service';
import { FileManagerService } from '@feature/file_manager/file_manager.service';
import { delay } from '@visiba/std';
import { CreateNewMessageAnalyticActions, CreateNewMessageController } from '../view_create_new_message.controller';
import { Logger } from '@visiba/logger';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { signal } from '@preact/signals-react';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { CommitResultStatus } from '@api_patient/generated/clientv2/models/commit-result-status';
import { QueryCommitStatus } from '@api_patient/generated/clientv2/models/query-commit-status';
import { ValidationResultType } from '@api_patient/generated/clientv2/models/validation-result-type';
import type { Basket } from '../basket.service';
import type { CommitSuccessResult } from '@api_patient/generated/clientv2/models';
import { GeneralApiService } from '@api_patient/general_api.service';
import { UniversalConfig } from '@application/universal_config.service';

const COMMIT_STATUS_RETRY_COUNT = 30;
const COMMIT_STATUS_DELAY = 1000;

@Injectable({
	scope: Scopes.Scoped,
})
export class ScreenConclusionController implements ControllerProtocol {
	public readonly payment = signal<null | { billId: number; commitId: number }>(null);
	public readonly termsOfServiceUrl = signal<string | null>(null);
	private basket: Basket | null = null;
	private readonly logger = new Logger('ScreenConclusionController');

	constructor(
		private readonly createNewMessageController: CreateNewMessageController,
		private readonly basketApiService: BasketApiService,
		private readonly fileManagerService: FileManagerService,
		private readonly analyticService: AnalyticService,
		private readonly generalApiService: GeneralApiService,
		private readonly universalConfig: UniversalConfig,
	) {}

	async whenMount() {
		this.analyticService.trackPageView({
			customTitle: 'Create new message: Conclusion',
		});

		this.basket = this.createNewMessageController.getCurrentBasket();

		if (!this.allowedToBeHere()) {
			this.analyticService.trackEvent(
				AnalyticCategories.CreateNewMessage,
				CreateNewMessageAnalyticActions.NotAllowedToBeHere,
				'Conclusion',
			);

			this.logger.logInformation('Not allowed to be here. Redirect to start screen.');
			this.createNewMessageController.goToScreen('start');

			return;
		}

		const policyDocumentsResult = await this.generalApiService.getPolicyDocuments(
			this.universalConfig.alias,
			this.universalConfig.selectedLanguageCode.peek(),
		);
		if (policyDocumentsResult.ok) {
			this.termsOfServiceUrl.value = policyDocumentsResult.ok.data.TermsOfServiceUrl;
		}
	}

	public async submit(
		formData: CellulaFormSubmitData,
	): AsyncResult<{ commitJobId?: number; paymentBillId?: number; messageConversationId?: number | null }, Error | string> {
		if (!this.basket) {
			throw new Error('No basket found');
		}

		const reason = this.createNewMessageController.getFlowSettings().predefinedDescriptionOfMatter ?? formData.message ?? '';

		// Post the reason text
		const addReasonResult = await this.basketApiService.postReason(this.basket.Id, this.basket.ProductId, reason);
		if (addReasonResult.err) return Err(addReasonResult.err);

		// Post files if there are any
		const fileTickets = this.fileManagerService.peakUploadedIdentities();
		if (fileTickets.length > 0) {
			const addFilesResult = await this.basketApiService.postFiles(
				this.basket.Id,
				this.basket.ProductId,
				fileTickets.map((identity) => ({
					FileReference: identity,
				})),
			);

			if (addFilesResult.err) return Err(addFilesResult.err);
		}

		// If the basket is valid, we can commit it.
		const commitResult = await this.basketApiService.commit(this.basket.Id);
		if (commitResult.err) return Err(commitResult.err);

		switch (commitResult.ok.data.Status) {
			case CommitResultStatus.ValidationErrors:
				if (commitResult.ok.data.ValidationResult === ValidationResultType.FormMissingResponse) {
					return Err(I18nAndL10n.translate('View.CreateNewMessage.ScreenConclusion.FormValidationError'));
				} else {
					return Err(I18nAndL10n.translate('Error.Patient.Unknown'));
				}
			case CommitResultStatus.PendingPayment:
				if (!commitResult.ok.data.PaymentBillId || !commitResult.ok.data.CommitJobId) {
					return Err(new NonRecoverableError('No payment bill id'));
				}

				return Ok({
					paymentBillId: commitResult.ok.data.PaymentBillId,
					commitJobId: commitResult.ok.data.CommitJobId,
				});
			case CommitResultStatus.CommitInProgress:
			case CommitResultStatus.ReservationCommitted:
			// Just continue
		}

		if (!commitResult.ok.data.CommitJobId) {
			return Err(new NonRecoverableError('No commit job id'));
		}

		// Then we have to get the commit status to find out if the commit was
		// successful.
		return await this.waitForCommitStatus(commitResult.ok.data.CommitJobId);
	}

	public async waitForCommitStatus(commitJobId: number): AsyncResult<{ messageConversationId: number | null }> {
		const commitStatusResult = await this.getCommitStatus(commitJobId);
		if (commitStatusResult.err) return Err(new NonRecoverableError(commitStatusResult.err.message));

		// If we got a messaging case Id, return it. Otherwise return null.
		return Ok({
			messageConversationId: commitStatusResult.ok.MessagingCases.length > 0 ? commitStatusResult.ok.MessagingCases[0].Id : null,
		});
	}

	private async getCommitStatus(commitJobId: number): AsyncResult<CommitSuccessResult> {
		for (let count = 0; count < COMMIT_STATUS_RETRY_COUNT; count++) {
			const commitStatusResult = await this.basketApiService.getCommitStatus(commitJobId);

			if (commitStatusResult.err) return Err(commitStatusResult.err);

			if (commitStatusResult.ok.data.Status === QueryCommitStatus.CompletedOk) {
				return Ok(commitStatusResult.ok.data);
			}

			await delay(COMMIT_STATUS_DELAY);
		}

		return Err(new Error('Waited too long for commit status.'));
	}

	private allowedToBeHere() {
		// If we don't have a basket, we can't be here.
		if (!this.basket) {
			this.logger.logInformation('No basket found.');

			return false;
		}

		// If we don't have flow data, we can't be here.
		const flowData = this.createNewMessageController.flowData.value.value;
		if (!flowData) {
			this.logger.logInformation('FlowData not set.');

			return false;
		}

		// If we have reason for visits, we need to have a selected reason for visit.
		if (flowData.reasonForVisits.length > 0 && !this.createNewMessageController.getSelectedReasonForVisit()) {
			this.logger.logInformation('No reason for visit selected.');

			return false;
		}

		return true;
	}
}

export class NonRecoverableError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'NonRecoverableError';
	}
}
